import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { io, type Socket } from 'socket.io-client'
import type { AgentChat, AgentMessage } from '@multiplayer-app/ai-agent-types'
import { SocketIOConfig, SocketIOEvents } from '@multiplayer-app/ai-agent-types'

import { useAgentRuntime, useAgentStore } from './AgentProvider'
import { ProxyTransport } from '../runtime/transports/ProxyTransport'
import { getGuestUserId } from '../utils/guestUserId'
import { getChatState, mapPersistedStatusToUiStatus } from '@/state/useAgentStore'

type SocketContextValue = {
  socket: Socket | null
  socketId?: string
}

const SocketContext = createContext<SocketContextValue | null>(null)

export interface SocketProviderProps {
  children: ReactNode
}

export const SocketProvider = ({ children }: SocketProviderProps) => {
  const runtime = useAgentRuntime()
  const upsertChat = useAgentStore((state) => state.upsertChat)
  const upsertChatState = useAgentStore((state) => state.upsertChatState)
  const contextKey = useAgentStore((state) => state.contextKey)
  const activeChatId = useAgentStore((state) => state.activeChatId)

  // Stable user id for the session (guest id is persisted by helper)
  const userId = useMemo(() => runtime.config.user?.id ?? getGuestUserId(), [runtime.config.user?.id])
  const transport = runtime.config.transport

  const [socket, setSocket] = useState<Socket | null>(null)
  const [socketId, setSocketId] = useState<string | undefined>(undefined)

  const contextKeyRef = useRef(contextKey)
  const upsertChatRef = useRef(upsertChat)
  const upsertChatStateRef = useRef(upsertChatState)
  const lastSubscribedChatIdRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    contextKeyRef.current = contextKey
    upsertChatRef.current = upsertChat
    upsertChatStateRef.current = upsertChatState
  }, [contextKey, upsertChat, upsertChatState])

  useEffect(() => {
    // Only connect if using proxy transport
    if (transport.mode !== 'proxy') {
      setSocket(null)
      setSocketId(undefined)
      runtime.setSocketClient(null)
      if (runtime.transport instanceof ProxyTransport) {
        runtime.transport.setSocketId(undefined)
      }
      return
    }

    const baseUrl = transport.baseUrl
    const url = new URL(baseUrl, window.location.origin)
    const socketUrl = `${url.protocol}//${url.host}`
    const socketNamespace = transport.socketNamespace
    const socketTargetUrl = socketNamespace ? `${socketUrl}${socketNamespace}` : socketUrl

    // Build extra headers and auth like ProxyTransport: apiKey takes precedence, else use headers
    const extraHeaders: Record<string, string> = {}
    const transportConfig = transport as { apiKey?: string; headers?: Record<string, string> }

    if (transportConfig.headers) {
      Object.assign(extraHeaders, transportConfig.headers)
    }

    const auth: Record<string, string> = {}
    const authValue =
      (transportConfig.apiKey && `Bearer ${transportConfig.apiKey}`) || transportConfig.headers?.['Authorization']

    if (authValue) {
      auth['authorization'] = authValue
      extraHeaders['Authorization'] = authValue
    }

    const socketPath = transport.socketPath ?? SocketIOConfig.Path
    const s = io(socketTargetUrl, {
      path: socketPath,
      auth,
      transports: ['websocket', 'polling'],
      withCredentials: true,
      ...(Object.keys(extraHeaders).length > 0 && { extraHeaders })
    })

    runtime.setSocketClient(s)
    setSocket(s)

    const onConnect = () => {
      setSocketId(s.id)
      if (runtime.transport instanceof ProxyTransport) {
        runtime.transport.setSocketId(s.id)
      }
    }

    const onDisconnect = () => {
      setSocketId(undefined)
      if (runtime.transport instanceof ProxyTransport) {
        runtime.transport.setSocketId(undefined)
      }
    }

    const onChatNew = (chat: AgentChat) => {
      // Only update if the chat matches the current context (avoid cross-context noise)
      if (chat.contextKey === contextKeyRef.current) {
        upsertChatRef.current(chat)
        const currentState = runtime.store.getState()
        const chatState = getChatState(currentState, chat.id)
        upsertChatStateRef.current(chat.id, {
          ...chatState,
          status: mapPersistedStatusToUiStatus(chat.status)
        })
      }
    }

    const onMessageNew = (message: AgentMessage) => {
      const currentState = runtime.store.getState()
      const chatState = getChatState(currentState, message.chat)

      const idx = chatState.messages.findIndex((msg) => msg.id === message.id)
      if (idx >= 0) {
        const updatedMessages = [...chatState.messages]
        updatedMessages[idx] = message
        upsertChatStateRef.current(message.chat, {
          ...chatState,
          messages: updatedMessages
        })
        return
      }
      upsertChatStateRef.current(message.chat, {
        ...chatState,
        messages: [...chatState.messages, message]
      })
    }

    const onChatUpdate = (chat: AgentChat) => {
      upsertChatRef.current(chat)
      const currentState = runtime.store.getState()
      const chatState = getChatState(currentState, chat.id)
      upsertChatStateRef.current(chat.id, {
        ...chatState,
        status: mapPersistedStatusToUiStatus(chat.status)
      })
    }

    s.on('connect', onConnect)
    s.on('disconnect', onDisconnect)
    s.on('connect_error', (error) => {
      // eslint-disable-next-line no-console
      console.error('Socket.IO connection error:', error.message)
    })

    s.on(SocketIOEvents.ChatNew, onChatNew)
    s.on(SocketIOEvents.MessageNew, onMessageNew)
    s.on(SocketIOEvents.ChatUpdate, onChatUpdate)

    return () => {
      // Ensure transport stops excluding its own socket id after unmount
      if (runtime.transport instanceof ProxyTransport) {
        runtime.transport.setSocketId(undefined)
      }
      runtime.setSocketClient(null)
      setSocketId(undefined)
      setSocket(null)
      s.disconnect()
    }
  }, [
    runtime,
    runtime.transport,
    transport.mode,
    transport.mode === 'proxy' ? transport.baseUrl : undefined,
    transport.mode === 'proxy' ? transport.apiKey : undefined,
    transport.mode === 'proxy' ? transport.socketPath : undefined,
    transport.mode === 'proxy' ? transport.socketNamespace : undefined,
    transport.mode === 'proxy' ? JSON.stringify(transport.headers ?? {}) : '',
    userId
  ])

  useEffect(() => {
    if (!socket) return

    const previousChatId = lastSubscribedChatIdRef.current

    if (previousChatId && previousChatId !== activeChatId) {
      socket.emit('chat:unsubscribe', { chatId: previousChatId })
    }

    if (activeChatId && activeChatId !== previousChatId) {
      socket.emit('chat:subscribe', { chatId: activeChatId })
      lastSubscribedChatIdRef.current = activeChatId
    }

    if (!activeChatId) {
      lastSubscribedChatIdRef.current = undefined
    }
  }, [socket, activeChatId])

  const value = useMemo<SocketContextValue>(() => ({ socket, socketId }), [socket, socketId])

  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>
}

export const useSocket = (): SocketContextValue => {
  const ctx = useContext(SocketContext)
  if (!ctx) {
    // No provider mounted (e.g. older integration); behave as disconnected.
    return { socket: null, socketId: undefined }
  }
  return ctx
}
